/**
 * REK-2186: Clerk-enabled social OAuth strategies for consumer auth surfaces.
 *
 * Reads clerk-js `__unstable__environment.userSettings.social` (or the
 * equivalent authenticatable list) behind one guarded accessor. Missing env /
 * non-Clerk builds → empty list (never blocks email OTP).
 *
 * Intersects with optional `auth.social_providers` feature flag (boolean
 * kill-switch + description JSON order/disabled).
 *
 * Must be called under `<ClerkProvider>` (same as `useSignIn` / `useSignUp`
 * on LemonadeLogin). Clerk is the sole auth engine (SSO Wave 3, REK-2166).
 */
import { useMemo } from 'react';
import { useClerk, useSession } from '@clerk/clerk-react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  AUTH_SOCIAL_PROVIDERS_FLAG_CATEGORY,
  AUTH_SOCIAL_PROVIDERS_FLAG_KEY,
  applySocialProvidersFlag,
  parseSocialProvidersFlagDescription,
} from '@/config/feature-flags';
import { normalizeOauthStrategy } from '@/lib/clerk-auth-flows';
import { safeDebug } from '@/lib/safeLog';

export interface EnabledSocialStrategy {
  strategy: `oauth_${string}`;
  /** Display name from Clerk (`Google`, `Facebook`, …). */
  name: string;
  /** Optional Clerk-hosted logo URL. */
  logoUrl: string | null;
}

type ClerkSocialEntry = {
  enabled?: boolean;
  authenticatable?: boolean;
  strategy?: string;
  name?: string;
  logo_url?: string;
  logoUrl?: string;
};

/**
 * Pure extraction — exported for unit tests. Accepts the unstable Clerk
 * environment blob (or null) and returns authenticatable `oauth_*` entries.
 */
export function extractAuthenticatableSocialStrategies(
  environment: unknown,
): EnabledSocialStrategy[] {
  if (!environment || typeof environment !== 'object') return [];
  const userSettings = (environment as { userSettings?: unknown }).userSettings;
  if (!userSettings || typeof userSettings !== 'object') return [];

  const social = (userSettings as { social?: unknown }).social;
  if (!social || typeof social !== 'object') return [];

  const out: EnabledSocialStrategy[] = [];
  for (const [key, raw] of Object.entries(social as Record<string, unknown>)) {
    const entry = (raw ?? {}) as ClerkSocialEntry;
    const strategyRaw =
      (typeof entry.strategy === 'string' && entry.strategy) || key;
    const strategy = normalizeOauthStrategy(strategyRaw);
    if (!strategy) continue;
    if (entry.enabled === false) continue;
    if (entry.authenticatable === false) continue;
    // Staging FAPI marks usable social with authenticatable:true (and usually
    // enabled:true). Require at least one of those affirmative signals.
    if (entry.authenticatable !== true && entry.enabled !== true) continue;

    const name =
      (typeof entry.name === 'string' && entry.name.trim()) ||
      strategy.replace(/^oauth_/i, '');
    const logoUrl =
      (typeof entry.logoUrl === 'string' && entry.logoUrl) ||
      (typeof entry.logo_url === 'string' && entry.logo_url) ||
      null;
    out.push({ strategy, name, logoUrl });
  }
  return out;
}

function readClerkEnvironment(clerk: ReturnType<typeof useClerk>): unknown {
  const withUnstable = clerk as {
    __unstable__environment?: unknown;
    environment?: unknown;
  };
  return withUnstable.__unstable__environment ?? withUnstable.environment ?? null;
}

export function useEnabledSocialStrategies(): {
  strategies: EnabledSocialStrategy[];
  isLoading: boolean;
} {
  const clerk = useClerk();
  // Clerk often mutates `__unstable__environment` on the same client instance
  // after first paint. `useSession().isLoaded` flips when hydration finishes and
  // forces a recompute — do NOT memo solely on `clerk` identity.
  const { isLoaded: clerkLoaded } = useSession();
  const { flags, isLoading: flagsLoading } = useFeatureFlags(
    AUTH_SOCIAL_PROVIDERS_FLAG_CATEGORY,
  );

  const strategies = useMemo(() => {
    let clerkStrategies: EnabledSocialStrategy[] = [];
    if (clerkLoaded) {
      try {
        clerkStrategies = extractAuthenticatableSocialStrategies(
          readClerkEnvironment(clerk),
        );
      } catch (err) {
        safeDebug('SSO: failed to read Clerk social environment:', err);
      }
    }

    const flag = (flags ?? []).find(
      (f) => f.flag_key === AUTH_SOCIAL_PROVIDERS_FLAG_KEY,
    );
    const flagEnabled = flag ? flag.enabled : null;
    const config = parseSocialProvidersFlagDescription(flag?.description ?? null);
    return applySocialProvidersFlag(clerkStrategies, { flagEnabled, config });
  }, [clerk, clerkLoaded, flags]);

  return {
    strategies,
    isLoading: !clerkLoaded || flagsLoading,
  };
}
